// src/pages/menu/LocationTablesAndOrdersDashboard.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  Badge,
  Avatar,
  useTheme,
  alpha,
  Pagination,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Paper,
  LinearProgress,
  Divider,
  Stack,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  RestaurantMenu as MenuIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  TableRestaurant as TableRestaurantIcon,
  Send as SendIcon,
  Print as PrintIcon,
  Kitchen as KitchenIcon,
  LocalBar as LocalBarIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  Chair as ChairIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

// Import components
import Header from '../../../components/RestaurantPOS/waiters/Header';
import Footer from '../../../components/RestaurantPOS/waiters/Footer';
import Preloader from '../../../theme/Preloader';
import restaurantConfig from '../../../config/Organisation_Details';

// Import styles
import '../../../styles/Waiters.css';
import '../../../styles/ThermalReceipt.css';

// Import image
import foodImage from '../../../assets/f&b.jpg';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const ITEMS_PER_PAGE = 8;

// ============================================
// LOCATION CONFIGURATION
// ============================================

const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: '#2E7D32',
    accentColor: '#C62828',
    lightColor: '#4CAF50',
    icon: '🌍',
    path: '/african-tables'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: '#1565C0',
    accentColor: '#C62828',
    lightColor: '#1976D2',
    icon: '🌐',
    path: '/continental-tables'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: '#6A1B9A',
    accentColor: '#C62828',
    lightColor: '#9C27B0',
    icon: '🍸',
    path: '/lounge-tables'
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  if (!amount && amount !== 0) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case 'PENDING': return '#FF9800';
    case 'IN_PROGRESS': return '#2196F3';
    case 'READY': return '#4CAF50';
    case 'COMPLETED': return '#9E9E9E';
    case 'CANCELLED': return '#F44336';
    default: return '#9E9E9E';
  }
};

const getStatusLabel = (status) => {
  switch (status?.toUpperCase()) {
    case 'PENDING': return 'Pending';
    case 'IN_PROGRESS': return 'In Progress';
    case 'READY': return 'Ready';
    case 'COMPLETED': return 'Completed';
    case 'CANCELLED': return 'Cancelled';
    default: return status || 'Unknown';
  }
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[TablesOrders] Error checking token expiry:', err);
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    console.error('[TablesOrders] Error getting token expiry:', err);
    return 0;
  }
};

// ============================================
// API HELPERS
// ============================================

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[TablesOrders] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[TablesOrders] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[TablesOrders] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[TablesOrders] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[TablesOrders] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const url = `${FULL_API_URL}${endpoint}`;
  console.debug(`[TablesOrders] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.debug('[TablesOrders] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[TablesOrders] API Error:', err.message);
    throw err;
  }
};

// ============================================
// MAIN COMPONENT
// ============================================

const LocationTablesAndOrdersDashboard = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get location config
  const locationConfig = LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african;
  const LOCATION_COLOR = locationConfig.color;
  const ACCENT_COLOR = locationConfig.accentColor;
  const LOCATION_NAME = locationConfig.name;
  const LOCATION_ID = locationConfig.id;
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [activeMenu, setActiveMenu] = useState('Tables');
  const [displayText, setDisplayText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  
  // Tables and orders data
  const [tables, setTables] = useState([]);
  const [allTables, setAllTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedTableOrders, setSelectedTableOrders] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  
  // Dialog states
  const [tableDetailOpen, setTableDetailOpen] = useState(false);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  
  // Menu items for adding to orders
  const [menuItems, setMenuItems] = useState([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  
  // Cart for adding items
  const [addItemsCart, setAddItemsCart] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isSubmittingAddition, setIsSubmittingAddition] = useState(false);
  
  // Printing state
  const [isPrinting, setIsPrinting] = useState(false);
  const [printProgress, setPrintProgress] = useState(0);
  
  // KOT/BOT ticket data for printing after addition
  const [kotTicketData, setKotTicketData] = useState(null);
  const [botTicketData, setBotTicketData] = useState(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  
  // UI states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // ============================================
  // REFS
  // ============================================
  
  const typingTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // ============================================
  // CONFIG
  // ============================================
  // ============================================
// CONFIG
// ============================================

// Set the display text based on location
const getDisplayText = () => {
  if (locationKey === 'lounge') {
    return 'Velvet Terrace';
  }
  // For other locations, use restaurant name or location name
  return restaurantConfig.name || LOCATION_NAME || 'Restaurant';
};

const fullText = getDisplayText();
const currency = restaurantConfig.currency || '₦';
const waiterId = parseInt(localStorage.getItem('user_id')) || null;
const restaurantName = restaurantConfig.name || 'My Elegante';
const restaurantPhone = restaurantConfig.phone || '';
const restaurantAddress = restaurantConfig.address || '';
  
  // ============================================
  // TYPING EFFECT
  // ============================================
  
  useEffect(() => {
    if (isLoading) return;
    
    let index = 0;
    let isDeleting = false;
    
    const typeText = () => {
      if (!isMountedRef.current) return;
      
      if (!isDeleting) {
        if (index <= fullText.length) {
          setDisplayText(fullText.substring(0, index));
          index++;
          typingTimeoutRef.current = setTimeout(typeText, 100);
        } else {
          typingTimeoutRef.current = setTimeout(() => {
            isDeleting = true;
            index = fullText.length;
            typingTimeoutRef.current = setTimeout(typeText, 50);
          }, 3000);
        }
      } else {
        if (index >= 0) {
          setDisplayText(fullText.substring(0, index));
          index--;
          typingTimeoutRef.current = setTimeout(typeText, 50);
        } else {
          isDeleting = false;
          index = 0;
          typingTimeoutRef.current = setTimeout(typeText, 500);
        }
      }
    };
    
    typingTimeoutRef.current = setTimeout(typeText, 500);
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      isMountedRef.current = false;
    };
  }, [isLoading, fullText]);
  
  // ============================================
  // DATA FETCHING
  // ============================================
  
 const fetchTablesAndOrders = useCallback(async () => {
  try {
    console.debug(`[TablesOrders] Fetching tables and orders for ${LOCATION_NAME} (ID: ${LOCATION_ID})...`);
    setIsFetching(true);
    
    const tablesData = await apiRequest(`/tables?location_id=${LOCATION_ID}&limit=100`);
    console.debug('[TablesOrders] Tables fetched:', tablesData);
    
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    console.debug(`[TablesOrders] Filtering orders for date: ${todayStr}`);
    
    // Fetch only today's orders
    const ordersData = await apiRequest(`/orders/?location_id=${LOCATION_ID}&status=PENDING,IN_PROGRESS&date_from=${todayStr}&date_to=${todayStr}&limit=100`);
    console.debug('[TablesOrders] Orders fetched:', ordersData);
    
    if (isMountedRef.current) {
      const tablesList = Array.isArray(tablesData) ? tablesData : [];
      let ordersList = [];
      if (ordersData && typeof ordersData === 'object') {
        if (Array.isArray(ordersData.items)) {
          ordersList = ordersData.items;
        } else if (Array.isArray(ordersData)) {
          ordersList = ordersData;
        }
      }
      
      // Additional filter to ensure only today's orders are shown
      const todayStr = new Date().toISOString().split('T')[0];
      ordersList = ordersList.filter(order => {
        const orderDate = order.order_date || order.created_date || order.order_time;
        if (!orderDate) return true;
        const orderDateStr = new Date(orderDate).toISOString().split('T')[0];
        return orderDateStr === todayStr;
      });
      
      const occupiedTableIds = ordersList
        .filter(order => order.table_id)
        .map(order => order.table_id);
      
      const updatedTables = tablesList.map(table => ({
        ...table,
        is_occupied: occupiedTableIds.includes(table.id) || table.status === 'OCCUPIED',
        status: occupiedTableIds.includes(table.id) ? 'OCCUPIED' : (table.status || 'AVAILABLE'),
      }));
      
      setAllTables(updatedTables);
      setTables(updatedTables.filter(t => t.status === 'OCCUPIED' || t.is_occupied));
      setOrders(ordersList);
      setFilteredOrders(ordersList);
      
      console.debug(`[TablesOrders] Loaded ${updatedTables.length} tables, ${ordersList.length} active orders for today`);
    }
  } catch (err) {
    console.error('[TablesOrders] Error fetching tables and orders:', err);
    if (isMountedRef.current) {
      setSnackbar({
        open: true,
        message: `Failed to fetch tables and orders: ${err.message}`,
        severity: 'error',
      });
    }
  } finally {
    if (isMountedRef.current) {
      setIsFetching(false);
    }
  }
}, [LOCATION_ID, LOCATION_NAME]);
  
  const fetchMenuItemsForLocation = useCallback(async () => {
    try {
      console.debug(`[TablesOrders] Fetching menu items for ${LOCATION_NAME}...`);
      const data = await apiRequest(`/menu-items/location/${LOCATION_NAME}?limit=1000`);
      console.debug('[TablesOrders] Menu items fetched:', data);
      
      if (isMountedRef.current) {
        const itemsData = Array.isArray(data) ? data : [];
        const activeItems = itemsData.filter(item => item.is_active === 1 || item.is_active === true);
        setMenuItems(activeItems);
        setFilteredMenuItems(activeItems);
        console.debug(`[TablesOrders] Loaded ${activeItems.length} active menu items`);
      }
    } catch (err) {
      console.error('[TablesOrders] Error fetching menu items:', err);
    }
  }, [LOCATION_NAME]);
  
  const fetchCategoriesForLocation = useCallback(async () => {
    try {
      console.debug(`[TablesOrders] Fetching categories for ${LOCATION_NAME}...`);
      const data = await apiRequest(`/categories/location/${LOCATION_NAME}?limit=100`);
      console.debug('[TablesOrders] Categories fetched:', data);
      
      if (isMountedRef.current) {
        const categoriesData = Array.isArray(data) ? data : [];
        setCategories(categoriesData.filter(c => c.is_active === 1 || c.is_active === true));
      }
    } catch (err) {
      console.error('[TablesOrders] Error fetching categories:', err);
    }
  }, [LOCATION_NAME]);
  
  const fetchAllData = useCallback(async () => {
    await fetchTablesAndOrders();
    await fetchMenuItemsForLocation();
    await fetchCategoriesForLocation();
  }, [fetchTablesAndOrders, fetchMenuItemsForLocation, fetchCategoriesForLocation]);
  
  // ============================================
  // INITIALIZATION
  // ============================================
  
  useEffect(() => {
    isMountedRef.current = true;
    
    console.debug(`[TablesOrders] Initializing for ${LOCATION_NAME}...`);
    
    fetchAllData();
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 1500);
    
    return () => {
      clearTimeout(timer);
      isMountedRef.current = false;
    };
  }, [fetchAllData, LOCATION_NAME]);
  
  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================
  
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();
      if (timeLeft > 0 && timeLeft < 120000) {
        await refreshAuthToken();
      }
    };
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);
  
  // ============================================
  // FILTERING & SEARCH
  // ============================================
  
  const applyFilters = useCallback(() => {
    let filtered = [...orders];
    
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(order => 
        order.order_status?.toUpperCase() === statusFilter
      );
    }
    
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(order => {
        const orderNumber = order.order_number?.toLowerCase() || '';
        const customerName = order.customer_name?.toLowerCase() || '';
        const tableName = allTables.find(t => t.id === order.table_id)?.table_name?.toLowerCase() || '';
        return orderNumber.includes(term) || customerName.includes(term) || tableName.includes(term);
      });
    }
    
    filtered.sort((a, b) => new Date(b.order_time) - new Date(a.order_time));
    setFilteredOrders(filtered);
    setPage(1);
  }, [orders, statusFilter, searchTerm, allTables]);
  
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(applyFilters, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [applyFilters]);
  
  // ============================================
  // TABLE DETAIL HANDLING
  // ============================================
  
  const handleTableClick = useCallback((table) => {
    setSelectedTable(table);
    const tableOrders = orders.filter(order => order.table_id === table.id);
    setSelectedTableOrders(tableOrders);
    setTableDetailOpen(true);
  }, [orders]);
  
  const handleCloseTableDetail = useCallback(() => {
    setTableDetailOpen(false);
    setSelectedTable(null);
    setSelectedTableOrders([]);
  }, []);
  
  // ============================================
  // ADD ITEMS TO ORDER
  // ============================================
  
  const handleAddItemsToOrder = useCallback((orderId) => {
    setSelectedOrderId(orderId);
    setAddItemsCart([]);
    setKotTicketData(null);
    setBotTicketData(null);
    setAddItemDialogOpen(true);
  }, []);
  
  const addToCart = useCallback((item) => {
    if (!item || !item.id) return;
    setIsAddingToCart(true);
    try {
      setAddItemsCart(prevCart => {
        const existingIndex = prevCart.findIndex(cartItem => cartItem.id === item.id);
        if (existingIndex >= 0) {
          const newCart = [...prevCart];
          newCart[existingIndex] = { ...newCart[existingIndex], quantity: newCart[existingIndex].quantity + 1 };
          return newCart;
        }
        return [...prevCart, {
          id: item.id,
          name: item.name || 'Unknown Item',
          amount: parseFloat(item.amount) || 0,
          category: item.category || 'Uncategorized',
          main_category: item.main_category || 'Food',
          quantity: 1,
        }];
      });
    } catch (error) {
      console.error('[TablesOrders] Error adding to cart:', error);
    } finally {
      setIsAddingToCart(false);
    }
  }, []);
  
  const removeFromCart = useCallback((itemId) => {
    setAddItemsCart(prevCart => prevCart.filter(item => item.id !== itemId));
  }, []);
  
  const updateQuantity = useCallback((itemId, quantity) => {
    if (quantity <= 0) { removeFromCart(itemId); return; }
    setAddItemsCart(prevCart =>
      prevCart.map(item => item.id === itemId ? { ...item, quantity } : item)
    );
  }, [removeFromCart]);
  
  const clearCart = useCallback(() => setAddItemsCart([]), []);
  
  const getCartTotal = useCallback(() => {
    return addItemsCart.reduce((total, item) => total + (item.amount || 0) * item.quantity, 0);
  }, [addItemsCart]);
  
  const getCartItemCount = useCallback(() => {
    return addItemsCart.reduce((total, item) => total + item.quantity, 0);
  }, [addItemsCart]);
  
  // ============================================
  // GENERATE TICKET HTML FUNCTION
  // ============================================
  
  const generateTicketHTML = useCallback((ticketData) => {
    const ticketTotal = ticketData.total || ticketData.items.reduce((sum, item) => 
      sum + (item.amount || item.price || 0) * (item.quantity || 1), 0
    );
    
    const formatCurrencyLocal = (amount) => {
      return `${currency} ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    
    const formatDateLocal = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };
    
    const formatTimeLocal = (timeString) => {
      if (!timeString) return 'N/A';
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };
    
    return `
      <div id="receipt-content" style="max-width:80mm;margin:0 auto;padding:8px 6px;background:#ffffff;font-family:'Courier New',monospace;font-size:12px;line-height:1.6;color:#000000;font-weight:bold;">
        <div style="text-align:center;padding-bottom:8px;border-bottom:2px solid #000;margin-bottom:8px;">
          <div style="font-size:18px;font-weight:bold;letter-spacing:2px;">${restaurantName}</div>
          ${restaurantAddress ? `<div style="font-size:9px;font-weight:bold;">${restaurantAddress}</div>` : ''}
          ${restaurantPhone ? `<div style="font-size:9px;font-weight:bold;">Tel: ${restaurantPhone}</div>` : ''}
          <div style="border-top:2px solid #000;margin:6px 0;"></div>
          <div style="font-size:16px;font-weight:bold;letter-spacing:2px;">${ticketData.ticket_title || 'ORDER TICKET'}</div>
          <div style="display:inline-block;padding:4px 16px;border:2px solid #000;font-size:14px;font-weight:bold;letter-spacing:2px;background-color:${ticketData.department === 'Kitchen' ? '#d4edda' : '#cce5ff'};color:${ticketData.department === 'Kitchen' ? '#155724' : '#004085'};margin-top:4px;">
            ${ticketData.department.toUpperCase()} COPY
          </div>
          <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:9px;font-weight:bold;margin-top:4px;">
            <span>${formatDateLocal(ticketData.order_date)}</span>
            <span>${formatTimeLocal(ticketData.order_time)}</span>
          </div>
        </div>
        
        <div style="margin-bottom:4px;">
          <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:12px;font-weight:bold;">
            <span>ORDER: ${ticketData.order_number || 'N/A'}</span>
            <span>#${String(ticketData.serial_number || 0).padStart(6, '0')}</span>
          </div>
          ${ticketData.order_type === 'DINE_IN' && ticketData.table_name ? `
            <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:11px;font-weight:bold;">
              <span>Table: ${ticketData.table_name}</span>
              <span>DINE IN</span>
            </div>
          ` : ''}
          ${ticketData.order_type === 'TAKEAWAY' ? `
            <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:11px;font-weight:bold;">
              <span>TAKEAWAY</span>
            </div>
          ` : ''}
          ${ticketData.customer_name ? `
            <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:11px;font-weight:bold;">
              <span>Customer: ${ticketData.customer_name}</span>
            </div>
          ` : ''}
          ${ticketData.waiter_name ? `
            <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:11px;font-weight:bold;">
              <span>Waiter: ${ticketData.waiter_name}</span>
            </div>
          ` : ''}
        </div>
        
        <div style="border-top:2px solid #000;margin:6px 0;"></div>
        
        <div style="margin-bottom:4px;">
          <div style="display:flex;justify-content:space-between;padding:4px 2px;border-bottom:2px solid #000;font-weight:bold;font-size:10px;background-color:#f5f5f5;">
            <span style="flex:1;">ITEM</span>
            <span style="min-width:25px;text-align:center;">QTY</span>
            <span style="min-width:60px;text-align:right;">TOTAL</span>
          </div>
          ${ticketData.items.map((item, idx) => {
            const quantity = item.quantity || 1;
            const name = item.name || 'Item';
            const price = item.amount || item.price || 0;
            const total = price * quantity;
            const notes = item.notes || '';
            return `
              <div style="margin-bottom:2px;">
                <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:11px;font-weight:bold;border-bottom:1px solid #000;">
                  <span style="flex:1;">${name}</span>
                  <span style="min-width:25px;text-align:center;">${quantity}</span>
                  <span style="min-width:60px;text-align:right;">${formatCurrencyLocal(total)}</span>
                </div>
                ${notes ? `<div style="padding-left:8px;margin-bottom:2px;"><span style="font-size:9px;color:#333;font-style:italic;font-weight:bold;">* ${notes}</span></div>` : ''}
              </div>
            `;
          }).join('')}
        </div>
        
        <div style="border-top:2px solid #000;margin:6px 0;"></div>
        <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;font-weight:bold;">
          <span>TICKET TOTAL</span>
          <span>${formatCurrencyLocal(ticketTotal)}</span>
        </div>
        
        ${ticketData.notes ? `
          <div style="border-top:2px solid #000;margin:6px 0;"></div>
          <div style="margin-bottom:4px;">
            <div style="font-size:9px;color:#666;text-transform:uppercase;font-weight:bold;">Notes</div>
            <div style="font-size:10px;color:#333;font-weight:bold;">${ticketData.notes}</div>
          </div>
        ` : ''}
        
        <div style="text-align:center;padding-top:8px;border-top:2px solid #000;margin-top:8px;">
          <div style="font-size:12px;font-weight:bold;">${ticketData.department} Copy</div>
          <div style="font-size:10px;font-weight:bold;">Please prepare for order #${ticketData.order_number}</div>
          <div style="border-top:2px solid #000;margin:6px 0;"></div>
          <div style="margin-top:4px;padding-top:2px;display:flex;justify-content:center;gap:2px;flex-wrap:wrap;">
            ${Array(30).fill(0).map(() => `<span style="width:2px;height:2px;border-radius:50%;background-color:#999;display:inline-block;"></span>`).join('')}
          </div>
          <div style="font-size:7px;color:#aaa;margin-top:2px;font-weight:bold;">${restaurantName} • ${ticketData.department} Copy</div>
        </div>
      </div>
    `;
  }, [restaurantName, restaurantAddress, restaurantPhone, currency]);
  
  // ============================================
  // PRINT TICKET FUNCTION
  // ============================================
  
  const printTicket = useCallback((ticketData, ticketType) => {
    try {
      setIsPrinting(true);
      setPrintProgress(50);
      
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '80mm';
      tempContainer.style.backgroundColor = 'white';
      
      const ticketHTML = generateTicketHTML(ticketData);
      tempContainer.innerHTML = ticketHTML;
      document.body.appendChild(tempContainer);
      
      const ticketElement = tempContainer.querySelector('#receipt-content');
      if (!ticketElement) {
        document.body.removeChild(tempContainer);
        throw new Error('Ticket content not found');
      }
      
      const allStyles = document.querySelectorAll('style');
      let combinedStyles = '';
      allStyles.forEach(style => { combinedStyles += style.innerHTML; });
      
      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>${ticketType} - ${ticketData.order_number}</title>
            <style>
              ${combinedStyles}
              @media print {
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { margin: 0; padding: 0; background: white; width: 80mm; }
                #receipt-content { max-width: 80mm !important; margin: 0 auto !important; padding: 8px 6px !important; background: white !important; font-family: 'Courier New', monospace !important; font-size: 12px !important; }
                .ticket-badge, .status-badge { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                .MuiTypography-root { color: black !important; }
                @page { size: 80mm auto !important; margin: 0 !important; padding: 0 !important; }
              }
              body { background: #f0f0f0; display: flex; flex-direction: column; align-items: center; padding: 20px; }
              #receipt-content { max-width: 80mm; margin: 0 auto; padding: 10px 8px; background: white; box-shadow: 0 4px 12px rgba(0,0,0,0.1); border-radius: 4px; }
              .print-instructions { text-align: center; padding: 10px; margin-bottom: 10px; background: #e3f2fd; border-radius: 4px; font-family: Arial, sans-serif; font-size: 12px; max-width: 80mm; }
            </style>
          </head>
          <body>
            <div class="print-instructions">🖨️ Printing ${ticketType} - ${ticketData.department}<br><small>Order #${ticketData.order_number}</small></div>
            ${ticketElement.outerHTML}
            <script>
              window.onload = function() {
                setTimeout(function() { window.print(); setTimeout(function() { window.close(); }, 3000); }, 500);
              };
            <\/script>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank', 'width=400,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes');
      
      if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '0';
        iframe.style.width = '400px';
        iframe.style.height = '800px';
        document.body.appendChild(iframe);
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(printHTML);
        iframeDoc.close();
        setTimeout(() => {
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 5000);
        }, 500);
        setTimeout(() => {
          if (document.body.contains(tempContainer)) {
            document.body.removeChild(tempContainer);
          }
        }, 5000);
        setPrintProgress(100);
        setSnackbar({ open: true, message: `${ticketType} sent to printer via iframe`, severity: 'success' });
        setTimeout(() => { setIsPrinting(false); setPrintProgress(0); }, 2000);
        return;
      }
      
      printWindow.document.write(printHTML);
      printWindow.document.close();
      setPrintProgress(100);
      setSnackbar({ open: true, message: `${ticketType} sent to printer!`, severity: 'success' });
      setTimeout(() => {
        setIsPrinting(false);
        setPrintProgress(0);
        if (document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer);
        }
      }, 2000);
      
    } catch (error) {
      console.error('[TablesOrders] Error printing ticket:', error);
      setIsPrinting(false);
      setPrintProgress(0);
      const tempContainer = document.getElementById('temp-receipt-container');
      if (tempContainer && document.body.contains(tempContainer)) {
        document.body.removeChild(tempContainer);
      }
      setSnackbar({
        open: true,
        message: `Failed to print ${ticketType}: ${error.message}`,
        severity: 'error',
      });
    }
  }, [generateTicketHTML]);
  
  // ============================================
  // PRINT KOT AND BOT FUNCTIONS
  // ============================================
  
  const handlePrintKOT = useCallback((order) => {
    let items = [];
    try {
      items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
    } catch (e) { items = []; }
    
    const foodItems = items.filter(item => item.main_category === 'Food');
    if (foodItems.length === 0) {
      setSnackbar({ open: true, message: 'No food items to print KOT', severity: 'warning' });
      return;
    }
    
    const kotData = {
      order_number: order.order_number,
      serial_number: order.serial_number,
      order_type: order.order_type,
      table_name: order.table_name || null,
      waiter_name: order.waiter_name || 'Waiter',
      order_date: order.order_date || new Date().toISOString(),
      order_time: order.order_time || new Date().toISOString(),
      notes: order.notes || null,
      customer_name: order.customer_name || null,
      customer_phone: order.customer_phone || null,
      items: foodItems,
      ticket_type: 'KOT',
      ticket_title: 'KITCHEN ORDER TICKET',
      department: 'Kitchen',
      total: foodItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0),
    };
    printTicket(kotData, 'KOT');
  }, [printTicket]);
  
  const handlePrintBOT = useCallback((order) => {
    let items = [];
    try {
      items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
    } catch (e) { items = []; }
    
    const drinkItems = items.filter(item => item.main_category === 'Drinks');
    if (drinkItems.length === 0) {
      setSnackbar({ open: true, message: 'No drink items to print BOT', severity: 'warning' });
      return;
    }
    
    const botData = {
      order_number: order.order_number,
      serial_number: order.serial_number,
      order_type: order.order_type,
      table_name: order.table_name || null,
      waiter_name: order.waiter_name || 'Waiter',
      order_date: order.order_date || new Date().toISOString(),
      order_time: order.order_time || new Date().toISOString(),
      notes: order.notes || null,
      customer_name: order.customer_name || null,
      customer_phone: order.customer_phone || null,
      items: drinkItems,
      ticket_type: 'BOT',
      ticket_title: 'BAR ORDER TICKET',
      department: 'Bar',
      total: drinkItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0),
    };
    printTicket(botData, 'BOT');
  }, [printTicket]);
  
  // ============================================
  // SUBMIT ADD ITEMS - WITH AUTO PRINT
  // ============================================
  
  const handleSubmitAddItems = useCallback(async () => {
    if (addItemsCart.length === 0) {
      setSnackbar({ open: true, message: 'Please add at least one item', severity: 'warning' });
      return;
    }
    
    setIsSubmittingAddition(true);
    
    try {
      const order = orders.find(o => o.id === selectedOrderId);
      if (!order) throw new Error('Order not found');
      
      let existingItems = [];
      try {
        existingItems = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
      } catch (parseErr) {
        console.error('[TablesOrders] Error parsing order items:', parseErr);
        existingItems = [];
      }
      
      const newItems = addItemsCart.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.amount,
        total: item.amount * item.quantity,
        category: item.category,
        main_category: item.main_category,
        notes: null,
      }));
      
      const combinedItems = [...existingItems];
      newItems.forEach(newItem => {
        const existingIndex = combinedItems.findIndex(ex => ex.id === newItem.id);
        if (existingIndex >= 0) {
          combinedItems[existingIndex].quantity += newItem.quantity;
          combinedItems[existingIndex].total = combinedItems[existingIndex].quantity * combinedItems[existingIndex].price;
        } else {
          combinedItems.push(newItem);
        }
      });
      
      const subtotal = combinedItems.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
      const totalAmount = subtotal;
      
      const updateData = {
        items: combinedItems,
        subtotal: subtotal,
        tax: 0,
        discount: 0,
        total_amount: totalAmount,
        notes: order.notes || null,
      };
      
      console.debug('[TablesOrders] Updating order:', { orderId: selectedOrderId, updateData });
      
      const response = await apiRequest(`/orders/${selectedOrderId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      
      console.debug('[TablesOrders] Order updated:', response);
      
      // Update the order in state
      const updatedOrder = { ...order, ...response, items: combinedItems };
      setOrders(prevOrders => prevOrders.map(o => o.id === selectedOrderId ? updatedOrder : o));
      setSelectedTableOrders(prev => prev.map(o => o.id === selectedOrderId ? updatedOrder : o));
      
      setSnackbar({ open: true, message: `Added ${addItemsCart.length} item(s) to order successfully!`, severity: 'success' });
      
      // --- AUTO PRINT KOT AND/OR BOT ---
      const foodItems = combinedItems.filter(item => item.main_category === 'Food');
      const drinkItems = combinedItems.filter(item => item.main_category === 'Drinks');
      const hasFood = foodItems.length > 0;
      const hasDrinks = drinkItems.length > 0;
      
      const baseTicketData = {
        order_number: updatedOrder.order_number,
        serial_number: updatedOrder.serial_number,
        order_type: updatedOrder.order_type,
        table_name: updatedOrder.table_name || null,
        waiter_name: updatedOrder.waiter_name || 'Waiter',
        order_date: updatedOrder.order_date || new Date().toISOString(),
        order_time: updatedOrder.order_time || new Date().toISOString(),
        notes: updatedOrder.notes || null,
        customer_name: updatedOrder.customer_name || null,
        customer_phone: updatedOrder.customer_phone || null,
      };
      
      let kotData = null;
      let botData = null;
      
      if (hasFood) {
        kotData = {
          ...baseTicketData,
          items: foodItems,
          ticket_type: 'KOT',
          ticket_title: 'KITCHEN ORDER TICKET (ADDITION)',
          department: 'Kitchen',
          total: foodItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        };
      }
      
      if (hasDrinks) {
        botData = {
          ...baseTicketData,
          items: drinkItems,
          ticket_type: 'BOT',
          ticket_title: 'BAR ORDER TICKET (ADDITION)',
          department: 'Bar',
          total: drinkItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        };
      }
      
      // Store ticket data for receipt dialog
      setKotTicketData(kotData);
      setBotTicketData(botData);
      
      // Auto-print KOT if food items exist
      if (kotData) {
        setTimeout(() => {
          printTicket(kotData, 'KOT');
        }, 500);
      }
      
      // Auto-print BOT if drink items exist
      if (botData) {
        setTimeout(() => {
          printTicket(botData, 'BOT');
        }, 1000);
      }
      
      // Show receipt dialog with print options
      if (kotData || botData) {
        setReceiptDialogOpen(true);
      }
      
      // Refresh data
      await fetchAllData();
      
      // Clear cart but KEEP DIALOG OPEN
      setAddItemsCart([]);
      
    } catch (error) {
      console.error('[TablesOrders] Error adding items to order:', error);
      setSnackbar({ open: true, message: `Failed to add items: ${error.message}`, severity: 'error' });
    } finally {
      setIsSubmittingAddition(false);
    }
  }, [addItemsCart, selectedOrderId, orders, fetchAllData, printTicket]);
  
  // ============================================
  // MENU ITEMS FILTERING
  // ============================================
  
  const applyMenuFilters = useCallback(() => {
    let filtered = [...menuItems];
    if (selectedCategory !== 'ALL') {
      filtered = filtered.filter(item => item.category?.toLowerCase() === selectedCategory.toLowerCase());
    }
    if (menuSearchTerm && menuSearchTerm.trim()) {
      const term = menuSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => 
        item.name?.toLowerCase().includes(term) || 
        item.category?.toLowerCase().includes(term)
      );
    }
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    setFilteredMenuItems(filtered);
  }, [menuItems, selectedCategory, menuSearchTerm]);
  
  useEffect(() => {
    const timeout = setTimeout(applyMenuFilters, 300);
    return () => clearTimeout(timeout);
  }, [applyMenuFilters]);
  
  // ============================================
  // REFRESH DATA
  // ============================================
  
  const handleRefresh = useCallback(async () => {
    await fetchAllData();
    setSnackbar({ open: true, message: 'Data refreshed successfully', severity: 'success' });
  }, [fetchAllData]);
  
  // ============================================
  // RENDER HELPERS
  // ============================================
  
  const getStatusChip = useCallback((status) => {
    const color = getStatusColor(status);
    const label = getStatusLabel(status);
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: alpha(color, 0.12),
          color: color,
          fontWeight: 700,
          fontSize: '0.7rem',
          height: 24,
          '& .MuiChip-label': { px: 1.5 },
          border: `1px solid ${alpha(color, 0.25)}`,
        }}
      />
    );
  }, []);
  
  const getMainCategoryIcon = useCallback((mainCategory) => {
    if (mainCategory === 'Food') {
      return <RestaurantIcon sx={{ color: ACCENT_COLOR, fontSize: '1.1rem' }} />;
    } else if (mainCategory === 'Drinks') {
      return <DrinkIcon sx={{ color: '#1976D2', fontSize: '1.1rem' }} />;
    }
    return <FoodIcon sx={{ color: LOCATION_COLOR, fontSize: '1.1rem' }} />;
  }, [ACCENT_COLOR, LOCATION_COLOR]);
  
  // Import sidebar dynamically based on location
  let Sidebar;
  try {
    Sidebar = require(`../../../components/RestaurantPOS/waiters/${locationKey}/Sidebar`).default;
  } catch (e) {
    Sidebar = require('../../../components/RestaurantPOS/waiters/african/Sidebar').default;
  }
  
  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <Preloader text={`Loading ${LOCATION_NAME} Tables & Orders...`} size={70} fullScreen={true} />;
  }
  
  // ============================================
  // MAIN RENDER
  // ============================================
  
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  
  return (
    <>
      <div className="epos-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="epos-main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          
          <div className="epos-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            <Header />
            
            <Box sx={{
              flex: 1,
              overflow: 'auto',
              p: { xs: 2, sm: 3 },
              bgcolor: '#f5f7fa',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
            }}>
              {/* Hero Section */}
              <Box sx={{
                bgcolor: 'white',
                borderRadius: '16px',
                p: { xs: 2, sm: 3 },
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                border: `1px solid ${alpha(LOCATION_COLOR, 0.15)}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                <Box>
                  <Typography variant="h4" sx={{
                    color: LOCATION_COLOR,
                    fontWeight: 'bold',
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                  }}>
                    {displayText}
                    <span style={{
                      display: 'inline-block',
                      width: '3px',
                      height: '28px',
                      backgroundColor: LOCATION_COLOR,
                      marginLeft: '4px',
                      verticalAlign: 'text-bottom',
                      animation: 'blink 1s step-end infinite',
                    }} />
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {tables.length} occupied tables · {orders.length} active orders · {LOCATION_NAME}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip
                    icon={<span>{locationConfig.icon}</span>}
                    label={LOCATION_NAME}
                    sx={{
                      bgcolor: alpha(LOCATION_COLOR, 0.1),
                      color: LOCATION_COLOR,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      px: 1,
                      height: 36,
                      '& .MuiChip-label': { fontWeight: 700 },
                    }}
                  />
                  
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      onClick={handleRefresh} 
                      disabled={isFetching}
                      sx={{
                        bgcolor: alpha(LOCATION_COLOR, 0.1),
                        '&:hover': { bgcolor: alpha(LOCATION_COLOR, 0.2) },
                        width: 40,
                        height: 40,
                      }}
                    >
                      {isFetching ? <CircularProgress size={24} /> : <RefreshIcon sx={{ color: LOCATION_COLOR }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              {/* Filters */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 3,
                flexWrap: 'wrap',
              }}>
                <TextField
                  size="small"
                  placeholder="Search by order number, customer, or table..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    flex: 1,
                    minWidth: '200px',
                    maxWidth: { xs: '100%', sm: '450px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      bgcolor: 'white',
                      '& fieldset': { borderColor: alpha(LOCATION_COLOR, 0.2) },
                      '&:hover fieldset': { borderColor: LOCATION_COLOR },
                      '&.Mui-focused fieldset': { borderColor: LOCATION_COLOR },
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: '180px' } }}>
                  <InputLabel id="status-filter-label" sx={{ fontWeight: 600 }}>Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                    sx={{
                      borderRadius: '12px',
                      bgcolor: 'white',
                      fontWeight: 500,
                      '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                    }}
                  >
                    <MenuItem value="ALL">All Status</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                    <MenuItem value="READY">Ready</MenuItem>
                  </Select>
                </FormControl>
                
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', fontWeight: 500 }}>
                  {filteredOrders.length} orders found
                </Typography>
              </Box>
              
              {/* Tables Grid - FIXED: Added proper spacing between rows */}
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: LOCATION_COLOR }}>
                🪑 Occupied Tables ({tables.length})
              </Typography>
              
              {isFetching ? (
                <Grid container spacing={3}>
                  {[...Array(4)].map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Skeleton variant="rectangular" height={220} sx={{ borderRadius: '16px' }} />
                    </Grid>
                  ))}
                </Grid>
              ) : tables.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 8,
                  bgcolor: 'white',
                  borderRadius: '16px',
                  border: `2px dashed ${alpha(LOCATION_COLOR, 0.15)}`,
                }}>
                  <TableRestaurantIcon sx={{ fontSize: 72, color: 'text.disabled', opacity: 0.3 }} />
                  <Typography variant="h5" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                    No Occupied Tables
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {`You havent served any table yet`}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  {tables.map((table) => {
                    const tableOrders = orders.filter(o => o.table_id === table.id);
                    const tableTotal = tableOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
                    const itemCount = tableOrders.reduce((sum, o) => {
                      let items = [];
                      try {
                        items = typeof o.items === 'string' ? JSON.parse(o.items) : (o.items || []);
                      } catch (e) { items = []; }
                      return sum + items.reduce((s, i) => s + (i.quantity || 1), 0);
                    }, 0);
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={table.id}>
                        <Card
                          onClick={() => handleTableClick(table)}
                          sx={{
                            borderRadius: '16px',
                            border: `2px solid ${alpha(LOCATION_COLOR, 0.15)}`,
                            cursor: 'pointer',
                            transition: 'all 0.25s ease',
                            position: 'relative',
                            overflow: 'visible',
                            height: '100%',
                            minHeight: 240,
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              transform: 'translateY(-6px)',
                              boxShadow: theme.shadows[12],
                              borderColor: LOCATION_COLOR,
                            },
                            '&:active': { transform: 'scale(0.97)' },
                          }}
                        >
                          {/* Occupied Badge */}
                          <Box sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            bgcolor: ACCENT_COLOR,
                            color: 'white',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 3px 12px ${alpha(ACCENT_COLOR, 0.4)}`,
                            zIndex: 2,
                          }}>
                            <PeopleIcon sx={{ fontSize: 18 }} />
                          </Box>
                          
                          <CardContent sx={{ 
                            p: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5,
                            flex: 1,
                          }}>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 64,
                              height: 64,
                              borderRadius: '50%',
                              bgcolor: alpha(LOCATION_COLOR, 0.1),
                              color: LOCATION_COLOR,
                              transition: 'all 0.2s ease',
                              '&:hover': { transform: 'scale(1.05)' },
                            }}>
                              <TableRestaurantIcon sx={{ fontSize: 34 }} />
                            </Box>
                            
                            <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }}>
                              {table.table_name}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                              <Chip
                                icon={<PeopleIcon sx={{ fontSize: 14 }} />}
                                label={`${table.capacity || 4} seats`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                              />
                              <Chip
                                label={`${tableOrders.length} order${tableOrders.length !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{
                                  bgcolor: alpha(LOCATION_COLOR, 0.12),
                                  color: LOCATION_COLOR,
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                }}
                              />
                            </Box>
                            
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              width: '100%',
                              pt: 1.5,
                              mt: 'auto',
                              borderTop: `1px solid ${alpha('#000', 0.08)}`,
                            }}>
                              <Typography variant="body2" color="text.secondary" fontWeight={600} fontSize="0.8rem">
                                Items: {itemCount}
                              </Typography>
                              <Typography variant="h6" fontWeight={700} color={LOCATION_COLOR} fontSize="1rem">
                                {formatCurrency(tableTotal)}
                              </Typography>
                            </Box>
                          </CardContent>
                          
                          <CardActions sx={{ 
                            p: 1.5, 
                            pt: 0,
                            pb: 2,
                            borderTop: `1px solid ${alpha('#000', 0.06)}`,
                            mt: 'auto',
                          }}>
                            <Button
                              fullWidth
                              variant="contained"
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTableClick(table);
                              }}
                              sx={{
                                bgcolor: LOCATION_COLOR,
                                '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                py: 1,
                              }}
                              startIcon={<VisibilityIcon />}
                            >
                              View Orders
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              
              {/* Orders List */}
              <Divider sx={{ my: 4 }} />
              
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2.5,
                flexWrap: 'wrap',
                gap: 1,
              }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: LOCATION_COLOR }}>
                  📋 Active Orders ({filteredOrders.length})
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Showing {paginatedOrders.length} of {filteredOrders.length} orders
                </Typography>
              </Box>
              
              {isFetching ? (
                [...Array(3)].map((_, index) => (
                  <Skeleton key={index} variant="rectangular" height={90} sx={{ borderRadius: '14px', mb: 1.5 }} />
                ))
              ) : filteredOrders.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 6,
                  bgcolor: 'white',
                  borderRadius: '16px',
                  border: `2px dashed ${alpha('#000', 0.08)}`,
                }}>
                  <ReceiptIcon sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.3 }} />
                  <Typography variant="h5" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                    No orders found under you today
                  </Typography>
                 
                </Box>
              ) : (
                <>
                  <Stack spacing={2.5}>
                    {paginatedOrders.map((order) => {
                      const table = allTables.find(t => t.id === order.table_id);
                      let items = [];
                      try {
                        items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
                      } catch (e) { items = []; }
                      const itemCount = items.reduce((sum, i) => sum + (i.quantity || 1), 0);
                      const hasFood = items.some(item => item.main_category === 'Food');
                      const hasDrinks = items.some(item => item.main_category === 'Drinks');
                      
                      return (
                        <Paper
                          key={order.id}
                          elevation={0}
                          sx={{
                            p: { xs: 2, sm: 2.5 },
                            border: `1px solid ${alpha('#000', 0.06)}`,
                            borderRadius: '14px',
                            bgcolor: 'white',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: alpha(LOCATION_COLOR, 0.3),
                              boxShadow: theme.shadows[3],
                            },
                          }}
                        >
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            gap: 2,
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.5 }}>
                                <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                                  {order.order_number}
                                </Typography>
                                {getStatusChip(order.order_status)}
                                {table && (
                                  <Chip
                                    icon={<TableRestaurantIcon sx={{ fontSize: 14 }} />}
                                    label={table.table_name}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                                  />
                                )}
                                {order.order_type === 'TAKEAWAY' && (
                                  <Chip
                                    label="Takeaway"
                                    size="small"
                                    sx={{
                                      bgcolor: alpha('#1976D2', 0.1),
                                      color: '#1976D2',
                                      fontWeight: 700,
                                      fontSize: '0.6rem',
                                      height: 22,
                                    }}
                                  />
                                )}
                              </Box>
                              
                              <Box sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 2,
                                alignItems: 'center',
                              }}>
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', fontWeight: 500 }}>
                                  <ScheduleIcon sx={{ fontSize: 16 }} />
                                  {formatTime(order.order_time)}
                                </Typography>
                                
                                <Typography variant="body2" color="text.secondary" fontWeight={500} fontSize="0.8rem">
                                  {itemCount} item{itemCount !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                              
                              {/* ORDER ITEMS */}
                              {items.length > 0 && (
                                <Box sx={{ 
                                  mt: 1.5, 
                                  pt: 1.5, 
                                  borderTop: `1px dashed ${alpha('#000', 0.08)}`,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.75,
                                  alignItems: 'center',
                                }}>
                                  {items.slice(0, 8).map((item, idx) => (
                                    <Chip
                                      key={idx}
                                      label={`${item.name} ×${item.quantity || 1}`}
                                      size="small"
                                      sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        bgcolor: alpha(LOCATION_COLOR, 0.06),
                                        borderRadius: '6px',
                                        height: 28,
                                        mb: 0.5,
                                        '& .MuiChip-label': { 
                                          px: 1.5,
                                          py: 0.25,
                                        },
                                      }}
                                      icon={getMainCategoryIcon(item.main_category)}
                                    />
                                  ))}
                                  {items.length > 8 && (
                                    <Chip
                                      label={`+${items.length - 8} more`}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        bgcolor: alpha('#000', 0.04),
                                        height: 28,
                                        mb: 0.5,
                                      }}
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                            
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              flexShrink: 0,
                              width: { xs: '100%', md: 'auto' },
                              flexWrap: 'wrap',
                            }}>
                              <Typography variant="h5" fontWeight={700} color={LOCATION_COLOR} sx={{ fontSize: { xs: '1.1rem', sm: '1.2rem' } }}>
                                {formatCurrency(order.total_amount || 0)}
                              </Typography>
                              
                              {/* Print Buttons */}
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {hasFood && (
                                  <Tooltip title="Print Kitchen Order Ticket (KOT)">
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePrintKOT(order)}
                                      disabled={isPrinting}
                                      sx={{
                                        bgcolor: alpha('#4CAF50', 0.1),
                                        color: '#4CAF50',
                                        '&:hover': { bgcolor: alpha('#4CAF50', 0.2) },
                                        width: 32,
                                        height: 32,
                                      }}
                                    >
                                      <KitchenIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {hasDrinks && (
                                  <Tooltip title="Print Bar Order Ticket (BOT)">
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePrintBOT(order)}
                                      disabled={isPrinting}
                                      sx={{
                                        bgcolor: alpha('#1976D2', 0.1),
                                        color: '#1976D2',
                                        '&:hover': { bgcolor: alpha('#1976D2', 0.2) },
                                        width: 32,
                                        height: 32,
                                      }}
                                    >
                                      <LocalBarIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                              
                              <Tooltip title="Add items to this order">
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleAddItemsToOrder(order.id)}
                                  sx={{
                                    bgcolor: LOCATION_COLOR,
                                    '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    borderRadius: '8px',
                                    minWidth: 90,
                                  }}
                                  startIcon={<AddIcon fontSize="small" />}
                                >
                                  Add Items
                                </Button>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                  
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                          '& .MuiPaginationItem-root': {
                            borderRadius: '8px',
                            fontWeight: 600,
                          },
                          '& .Mui-selected': {
                            bgcolor: `${LOCATION_COLOR} !important`,
                            color: 'white',
                            '&:hover': { bgcolor: `${LOCATION_COLOR} !important` },
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </div>
        </div>
        <Footer />
      </div>
      
      {/* ============================================ */}
      {/* TABLE DETAIL DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={tableDetailOpen}
        onClose={handleCloseTableDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          borderRadius: '20px 20px 0 0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TableRestaurantIcon sx={{ color: LOCATION_COLOR, fontSize: 28 }} />
            <Typography variant="h5" component="span" fontWeight={700}>
              {selectedTable?.table_name || 'Table Details'}
            </Typography>
            {selectedTable && (
              <Chip
                label={selectedTable.is_occupied ? 'Occupied' : 'Available'}
                size="small"
                sx={{
                  bgcolor: selectedTable.is_occupied ? alpha(ACCENT_COLOR, 0.12) : alpha('#4CAF50', 0.12),
                  color: selectedTable.is_occupied ? ACCENT_COLOR : '#4CAF50',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>
          <IconButton onClick={handleCloseTableDetail} sx={{ bgcolor: alpha('#000', 0.04) }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {selectedTableOrders.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ReceiptIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.3 }} />
              <Typography variant="h5" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                No Active Orders
              </Typography>
              <Typography variant="body1" color="text.secondary">
                This table has no active orders
              </Typography>
            </Box>
          ) : (
            <Stack spacing={3}>
              {selectedTableOrders.map((order) => {
                let items = [];
                try {
                  items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
                } catch (e) { items = []; }
                
                return (
                  <Paper
                    key={order.id}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      border: `1px solid ${alpha('#000', 0.06)}`,
                      borderRadius: '14px',
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="h6" fontWeight={700}>
                          {order.order_number}
                        </Typography>
                        {getStatusChip(order.order_status)}
                        {order.order_type === 'TAKEAWAY' && (
                          <Chip
                            label="Takeaway"
                            size="small"
                            sx={{
                              bgcolor: alpha('#1976D2', 0.1),
                              color: '#1976D2',
                              fontWeight: 700,
                              fontSize: '0.6rem',
                              height: 22,
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={500} fontSize="0.8rem">
                          {formatTime(order.order_time)}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            handleCloseTableDetail();
                            handleAddItemsToOrder(order.id);
                          }}
                          sx={{
                            borderColor: LOCATION_COLOR,
                            color: LOCATION_COLOR,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            borderRadius: '8px',
                            '&:hover': {
                              borderColor: LOCATION_COLOR,
                              bgcolor: alpha(LOCATION_COLOR, 0.05),
                            },
                          }}
                          startIcon={<AddIcon fontSize="small" />}
                        >
                          Add Items
                        </Button>
                      </Box>
                    </Box>
                    
                    {/* Order Items */}
                    <Box sx={{ 
                      bgcolor: alpha('#000', 0.02),
                      borderRadius: '10px',
                      p: 2,
                    }}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.8rem' }}>
                        Order Items
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1.5,
                      }}>
                        {items.map((item, idx) => (
                          <Box key={idx} sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                            px: 0.5,
                            borderBottom: idx < items.length - 1 ? `1px solid ${alpha('#000', 0.06)}` : 'none',
                            minHeight: '40px',
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              {getMainCategoryIcon(item.main_category)}
                              <Typography variant="body2" fontWeight={600} fontSize="0.85rem">
                                {item.name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                ×{item.quantity || 1}
                              </Typography>
                              <Typography variant="body2" fontWeight={700} fontSize="0.85rem" color={LOCATION_COLOR}>
                                {formatCurrency((item.price || 0) * (item.quantity || 1))}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mt: 2,
                      pt: 1.5,
                      borderTop: `2px solid ${alpha('#000', 0.06)}`,
                    }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {items.reduce((sum, i) => sum + (i.quantity || 1), 0)} items
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color={LOCATION_COLOR}>
                        {formatCurrency(order.total_amount || 0)}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
        }}>
          <Button
            onClick={handleCloseTableDetail}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 4,
              py: 1,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ============================================ */}
      {/* ADD ITEMS TO ORDER DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={addItemDialogOpen}
        onClose={() => {
          if (window.confirm('Are you sure you want to close? Items in cart will be lost.')) {
            setAddItemDialogOpen(false);
            setSelectedOrderId(null);
            setAddItemsCart([]);
            setKotTicketData(null);
            setBotTicketData(null);
          }
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          borderRadius: '20px 20px 0 0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AddIcon sx={{ color: LOCATION_COLOR, fontSize: 28 }} />
            <Typography variant="h5" component="span" fontWeight={700}>
              Add Items to Order - {LOCATION_NAME}
            </Typography>
            <Chip
              label={`${getCartItemCount()} items in cart`}
              size="medium"
              sx={{
                bgcolor: alpha(LOCATION_COLOR, 0.12),
                color: LOCATION_COLOR,
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
          </Box>
          <IconButton 
            onClick={() => {
              if (addItemsCart.length > 0) {
                if (window.confirm('Are you sure you want to close? Items in cart will be lost.')) {
                  setAddItemDialogOpen(false);
                  setSelectedOrderId(null);
                  setAddItemsCart([]);
                  setKotTicketData(null);
                  setBotTicketData(null);
                }
              } else {
                setAddItemDialogOpen(false);
                setSelectedOrderId(null);
                setKotTicketData(null);
                setBotTicketData(null);
              }
            }} 
            sx={{ bgcolor: alpha('#000', 0.04) }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {/* Menu Items Section */}
            <Box sx={{ flex: 1 }}>
              <TextField
                size="medium"
                placeholder="Search menu items..."
                value={menuSearchTerm}
                onChange={(e) => setMenuSearchTerm(e.target.value)}
                fullWidth
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: 'white',
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                  endAdornment: menuSearchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setMenuSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 1.5,
                mb: 2,
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 2 },
              }}>
                <Button
                  size="medium"
                  onClick={() => setSelectedCategory('ALL')}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    px: 3,
                    py: 0.75,
                    borderRadius: '10px',
                    bgcolor: selectedCategory === 'ALL' ? LOCATION_COLOR : 'white',
                    color: selectedCategory === 'ALL' ? 'white' : 'text.primary',
                    border: `1px solid ${selectedCategory === 'ALL' ? LOCATION_COLOR : alpha('#000', 0.08)}`,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      bgcolor: selectedCategory === 'ALL' ? LOCATION_COLOR : alpha(LOCATION_COLOR, 0.08),
                    },
                  }}
                >
                  ALL
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    size="medium"
                    onClick={() => setSelectedCategory(cat.name)}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      px: 3,
                      py: 0.75,
                      borderRadius: '10px',
                      bgcolor: selectedCategory === cat.name ? LOCATION_COLOR : 'white',
                      color: selectedCategory === cat.name ? 'white' : 'text.primary',
                      border: `1px solid ${selectedCategory === cat.name ? LOCATION_COLOR : alpha('#000', 0.08)}`,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        bgcolor: selectedCategory === cat.name ? LOCATION_COLOR : alpha(LOCATION_COLOR, 0.08),
                      },
                    }}
                  >
                    {cat.name} ({menuItems.filter(m => m.category === cat.name).length})
                  </Button>
                ))}
              </Box>
              
              <Box sx={{
                maxHeight: '450px',
                overflowY: 'auto',
                pr: 0.5,
                '&::-webkit-scrollbar': { width: 5 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
              }}>
                {filteredMenuItems.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <MenuIcon sx={{ fontSize: 56, color: 'text.disabled', opacity: 0.3 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 600 }}>
                      No menu items found
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2.5}>
                    {filteredMenuItems.map((item) => (
                      <Grid item xs={6} sm={4} lg={3} xl={2.4} key={item.id}>
                        <Card
                          onClick={() => addToCart(item)}
                          sx={{
                            borderRadius: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: `1px solid ${alpha('#000', 0.06)}`,
                            height: '100%',
                            minHeight: '150px',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: theme.shadows[6],
                              borderColor: LOCATION_COLOR,
                            },
                            '&:active': { transform: 'scale(0.96)' },
                          }}
                        >
                          <CardContent sx={{ 
                            p: 2.5, 
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                          }}>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              mb: 1,
                            }}>
                              <img
                                src={foodImage}
                                alt={item.name}
                                style={{
                                  width: 64,
                                  height: 64,
                                  objectFit: 'contain',
                                  opacity: 0.85,
                                }}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{
                                fontSize: '0.85rem',
                                wordWrap: 'break-word',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                minHeight: '2.6em',
                                lineHeight: 1.3,
                              }}
                            >
                              {item.name}
                            </Typography>
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color={LOCATION_COLOR}
                              sx={{ fontSize: '1rem', mt: 0.5 }}
                            >
                              {formatCurrency(item.amount)}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Box>
            
            {/* Cart Section */}
            <Box sx={{
              width: { xs: '100%', md: '320px' },
              minWidth: { xs: 'auto', md: '320px' },
              borderLeft: { xs: 'none', md: `1px solid ${alpha('#000', 0.08)}` },
              borderTop: { xs: `1px solid ${alpha('#000', 0.08)}`, md: 'none' },
              pt: { xs: 2, md: 0 },
              pl: { xs: 0, md: 2 },
              mt: { xs: 2, md: 0 },
              maxHeight: { xs: '300px', md: '550px' },
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                🛒 Cart ({getCartItemCount()} items)
              </Typography>
              
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 2 },
              }}>
                {addItemsCart.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight={600}>
                      No items in cart
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Tap menu items to add
                    </Typography>
                  </Box>
                ) : (
                  addItemsCart.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        mb: 1.5,
                        bgcolor: alpha(LOCATION_COLOR, 0.04),
                        borderRadius: '10px',
                        border: `1px solid ${alpha('#000', 0.06)}`,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} fontSize="0.85rem" noWrap>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {formatCurrency(item.amount)} × {item.quantity}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(LOCATION_COLOR, 0.08),
                            '&:hover': { bgcolor: alpha(LOCATION_COLOR, 0.15) },
                          }}
                        >
                          <RemoveIcon sx={{ fontSize: '0.8rem' }} />
                        </IconButton>
                        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.85rem' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha(LOCATION_COLOR, 0.08),
                            '&:hover': { bgcolor: alpha(LOCATION_COLOR, 0.15) },
                          }}
                        >
                          <AddIcon sx={{ fontSize: '0.8rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => removeFromCart(item.id)}
                          color="error"
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: alpha('#f44336', 0.08),
                            '&:hover': { bgcolor: alpha('#f44336', 0.15) },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '0.8rem' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
              
              {addItemsCart.length > 0 && (
                <Box sx={{
                  borderTop: `2px solid ${alpha('#000', 0.08)}`,
                  pt: 2,
                  mt: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <Typography variant="h6" fontWeight={700}>
                    Total:
                  </Typography>
                  <Typography variant="h5" fontWeight={700} color={LOCATION_COLOR}>
                    {formatCurrency(getCartTotal())}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1.5,
          flexWrap: 'wrap',
          bgcolor: '#f8f9fa',
          borderRadius: '0 0 20px 20px',
        }}>
          <Button
            onClick={() => {
              if (addItemsCart.length > 0) {
                if (window.confirm('Are you sure you want to close? Items in cart will be lost.')) {
                  setAddItemDialogOpen(false);
                  setSelectedOrderId(null);
                  setAddItemsCart([]);
                  setKotTicketData(null);
                  setBotTicketData(null);
                }
              } else {
                setAddItemDialogOpen(false);
                setSelectedOrderId(null);
                setKotTicketData(null);
                setBotTicketData(null);
              }
            }}
            disabled={isSubmittingAddition}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 4,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAddItems}
            variant="contained"
            disabled={addItemsCart.length === 0 || isSubmittingAddition}
            sx={{
              bgcolor: LOCATION_COLOR,
              '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
              '&:disabled': { bgcolor: 'grey.300' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 5,
              py: 1.2,
              fontSize: '1rem',
            }}
            startIcon={isSubmittingAddition ? <CircularProgress size={22} color="inherit" /> : <SendIcon />}
          >
            {isSubmittingAddition ? 'Adding...' : 'Add to Order'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ============================================ */}
      {/* RECEIPT DIALOG - For KOT/BOT after addition */}
      {/* ============================================ */}
      <Dialog
        open={receiptDialogOpen}
        onClose={() => setReceiptDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            maxWidth: '400px',
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ReceiptIcon sx={{ color: LOCATION_COLOR }} />
            <Typography variant="h6" component="span" fontWeight={700}>
              Tickets Printed
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              {kotTicketData && (
                <Chip
                  label="KOT"
                  size="small"
                  sx={{
                    bgcolor: '#d4edda',
                    color: '#155724',
                    fontWeight: 'bold',
                    fontSize: '0.6rem',
                    height: '20px',
                    border: '1px solid #155724',
                  }}
                />
              )}
              {botTicketData && (
                <Chip
                  label="BOT"
                  size="small"
                  sx={{
                    bgcolor: '#cce5ff',
                    color: '#004085',
                    fontWeight: 'bold',
                    fontSize: '0.6rem',
                    height: '20px',
                    border: '1px solid #004085',
                  }}
                />
              )}
            </Box>
          </Box>
          <IconButton
            onClick={() => setReceiptDialogOpen(false)}
            sx={{ color: 'text.secondary' }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          pt: 3, 
          backgroundColor: '#f5f7fa',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}>
          {/* Print Progress */}
          {isPrinting && printProgress > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" fontWeight={600}>
                  Printing...
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {printProgress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={printProgress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: alpha(LOCATION_COLOR, 0.1),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: LOCATION_COLOR,
                  }
                }}
              />
            </Box>
          )}
          
          {/* Ticket Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
              Tickets have been sent to printer:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* KOT Ticket */}
              {kotTicketData && (
                <Paper 
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '2px solid #155724',
                    borderRadius: '12px',
                    bgcolor: '#f0fff4',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#d4edda', 
                      p: 1, 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <KitchenIcon sx={{ color: '#155724' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#155724' }}>
                        Kitchen Order Ticket (KOT)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {kotTicketData.items.length} items · {formatCurrency(kotTicketData.total)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => printTicket(kotTicketData, 'KOT')}
                    disabled={isPrinting}
                    startIcon={isPrinting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                    sx={{
                      bgcolor: '#155724',
                      '&:hover': { bgcolor: '#0d3b1f' },
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                    }}
                  >
                    Re-print KOT
                  </Button>
                </Paper>
              )}
              
              {/* BOT Ticket */}
              {botTicketData && (
                <Paper 
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '2px solid #004085',
                    borderRadius: '12px',
                    bgcolor: '#f0f8ff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#cce5ff', 
                      p: 1, 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <LocalBarIcon sx={{ color: '#004085' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#004085' }}>
                        Bar Order Ticket (BOT)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {botTicketData.items.length} items · {formatCurrency(botTicketData.total)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => printTicket(botTicketData, 'BOT')}
                    disabled={isPrinting}
                    startIcon={isPrinting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                    sx={{
                      bgcolor: '#004085',
                      '&:hover': { bgcolor: '#002b5c' },
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                    }}
                  >
                    Re-print BOT
                  </Button>
                </Paper>
              )}
            </Box>
          </Box>
          
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'white', 
            borderRadius: '8px', 
            border: '1px solid #e0e0e0',
            textAlign: 'center',
          }}>
            <Typography variant="body2" color="text.secondary">
              ✅ Tickets have been automatically sent to the printer.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You can re-print using the buttons above if needed.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
          bgcolor: '#f8f9fa',
        }}>
          <Button
            onClick={() => setReceiptDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          
          /* Fix for card overlapping */
          .MuiGrid-item {
            margin-bottom: 8px !important;
          }
          
          .MuiCard-root {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
          }
          
          .MuiCardContent-root {
            flex: 1 !important;
          }
          
          .MuiCardActions-root {
            margin-top: auto !important;
            padding-top: 8px !important;
            padding-bottom: 16px !important;
            border-top: 1px solid rgba(0,0,0,0.06) !important;
          }
          
          @media (max-width: 600px) {
            .MuiDialog-paper {
              margin: 8px !important;
              max-height: 95vh !important;
            }
            .MuiDialog-paper .MuiDialogTitle-root {
              padding: 12px 16px !important;
            }
            .MuiDialog-paper .MuiDialogContent-root {
              padding: 16px !important;
            }
            .MuiDialog-paper .MuiDialogActions-root {
              padding: 12px 16px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default LocationTablesAndOrdersDashboard;